/**
 * Fetch the authenticated user's identity from Cloudflare Access.
 *
 * CF Access exposes `/cdn-cgi/access/get-identity` on **every host that sits
 * behind a CF Access app**. Because the SPA is hosted on the same origin as
 * its own CF Access app (the drata.net portal hosts in `NET_PORTAL_API_HOSTS`),
 * this relative, same-origin request works
 * on whichever host serves the page. No CORS concern, no token plumbing.
 *
 * This is a **UX-only** signal. The audit trail is generated server-side in
 * vendor-global-service from the verified JWT (see
 * `vendor-global-service/src/middleware/cf-access.middleware.ts`).
 */
export interface CfAccessIdentity {
    email: string;
    name?: string;
    groups?: string[];
}

const LOCAL_DEV_IDENTITY: CfAccessIdentity = {
    email: 'dev@drata.local',
    name: 'Local Dev',
    groups: ['vendor-portal-admins'],
};

export async function fetchIdentity(): Promise<CfAccessIdentity> {
    // Local dev runs without CF Access in the loop — vendor-global-service
    // injects a stub editor user via `DRATA_ENV=local`. We mirror that email
    // here so the header reads sensibly without 404-ing on /cdn-cgi/.
    if (import.meta.env.DEV) {
        return LOCAL_DEV_IDENTITY;
    }

    const response = await fetch('/cdn-cgi/access/get-identity', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch CF Access identity: HTTP ${response.status.toString()}`);
    }

    const payload = (await response.json()) as {
        email?: string;
        name?: string;
        groups?: { name?: string }[] | string[];
    };

    if (!payload.email) {
        throw new Error('CF Access identity payload is missing `email`.');
    }

    // CF Access can return groups either as a string list or an object list
    // depending on the IdP integration. Normalize to string[].
    const groups: string[] = Array.isArray(payload.groups)
        ? payload.groups
              .map((g) => (typeof g === 'string' ? g : g.name))
              .filter((g): g is string => typeof g === 'string')
        : [];

    return {
        email: payload.email,
        name: payload.name,
        groups,
    };
}
